import type { ReactNode } from "react";
import { ResponsiveContainer } from "recharts";
import styles from "./ChartContainer.module.css";

export interface ChartContainerProps {
  /** True while the upstream query is in flight. */
  isLoading?: boolean;
  /** True when the query came back with zero rows. */
  isEmpty?: boolean;
  /** Optional custom empty-state message. */
  emptyMessage?: string;
  /** Optional minimum height. Defaults to 150px (modern dense dashboard height). */
  minHeight?: number;
  /** Use the dense skeleton (140px) instead of the standard one. */
  dense?: boolean;
  /** Render-prop child given the bounding-box height. */
  children: () => ReactNode;
}

/**
 * Standard wrapper for every chart-shaped widget.
 *
 * Handles the three universal states (loading skeleton, empty state,
 * data) and ensures every chart has the same vertical sizing, so a
 * dashboard with a mix of widget types renders evenly. Charts get
 * Recharts' `ResponsiveContainer` for free, so widget renderers don't
 * each re-implement the size-observer dance.
 *
 * Authors render the actual chart inside the render-prop so we can keep
 * the loading/empty branches outside the recharts subtree (otherwise
 * Recharts re-mounts the chart on every state flip and the entrance
 * animation runs twice).
 */
export function ChartContainer({
  isLoading,
  isEmpty,
  emptyMessage,
  minHeight = 150,
  dense,
  children,
}: ChartContainerProps) {
  if (isLoading) {
    return (
      <div className={dense ? styles.chartWrapDense : styles.chartWrap}>
        <div className={styles.skeletonChart} style={{ minHeight }} />
      </div>
    );
  }
  if (isEmpty) {
    return (
      <div className={dense ? styles.chartWrapDense : styles.chartWrap} style={{ minHeight }}>
        <p className={styles.empty}>
          {emptyMessage ?? "No rows returned for this query."}
        </p>
      </div>
    );
  }
  return (
    <div className={dense ? styles.chartWrapDense : styles.chartWrap} style={{ minHeight }}>
      <ResponsiveContainer width="100%" height="100%">
        <>{children()}</>
      </ResponsiveContainer>
    </div>
  );
}
